import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Group, GroupKind } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Alert, Badge, Button, Card, EmptyState, FilterBar, PageHeader, SelectField, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { SortableList } from '../../components/SortableList';
import { useToast } from '../../components/Toast';
import { GroupList } from './api';

function NewGroupForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<GroupKind>('standard');
  const create = useMutation({
    mutationFn: () => api.post('/groups', { body: { name, kind }, schema: Group }),
    onSuccess: async (g) => {
      await qc.invalidateQueries({ queryKey: ['groups'] });
      void navigate(`/manage/groups/${g.id}`);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <Card title={t('groupsUi.create.title')} className="mb-6">
      <form onSubmit={submit} className="space-y-4">
        <ErrorLine error={create.error} />
        <TextField label={t('groupsUi.create.name')} required maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
        <fieldset className="space-y-2">
          <legend className="mb-1 text-sm font-medium text-slate-800">{t('groupsUi.create.kind')}</legend>
          {GroupKind.options.map((k) => (
            <label key={k} className="flex items-start gap-3 text-sm">
              <input type="radio" name="group-kind" className="mt-0.5 h-5 w-5 accent-[var(--brand-primary)]" checked={kind === k} onChange={() => setKind(k)} />
              <span>
                <span className="font-medium">{t(`groupsUi.kind.${k}`)}</span>
                <span className="block text-slate-600">{t(`groupsUi.create.${k}`)}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={create.isPending || name.trim() === ''}>
            {t('groupsUi.create.submit')}
          </Button>
          <Button onClick={onDone}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

export function GroupsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const canManage = useCan('groups.manage');
  const [status, setStatus] = useState<'active' | 'archived' | 'all'>('active');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const key = ['groups', 'list', status];
  const q = useQuery({ queryKey: key, queryFn: () => api.get('/groups', { query: { status }, schema: GroupList }) });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.put('/groups/order', { body: { groupIds: ids }, schema: GroupList }),
    onSuccess: (list) => {
      qc.setQueryData(key, list);
      toast(t('groupsUi.orderSaved'));
    },
    onError: () => void qc.invalidateQueries({ queryKey: ['groups'] }),
  });

  const newButton = canManage ? (
    <Button variant="primary" onClick={() => setCreating(true)} disabled={creating}>
      {t('groupsUi.new')}
    </Button>
  ) : null;
  const needle = search.trim().toLowerCase();
  const rows = (q.data ?? []).filter((g) => !needle || g.name.toLowerCase().includes(needle));
  const canReorder = canManage && status === 'active' && needle === '';

  return (
    <>
      <PageHeader title={t('groupsUi.title')} description={t('groupsUi.description')} actions={newButton} />
      {creating ? <NewGroupForm onDone={() => setCreating(false)} /> : null}
      <FilterBar columns="sm:grid-cols-2">
        <TextField label={t('common.search')} type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
        <SelectField label={t('common.status')} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="active">{t('sitesUi.filter.active')}</option>
          <option value="archived">{t('sitesUi.filter.archived')}</option>
          <option value="all">{t('sitesUi.filter.all')}</option>
        </SelectField>
      </FilterBar>
      {canManage ? <p className="mb-3 text-sm text-slate-600">{canReorder ? t('groupsUi.orderHint') : t('groupsUi.orderDisabled')}</p> : null}
      <ErrorLine error={reorder.error} />
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState message={t('groupsUi.empty')} action={newButton} />
      ) : (
        <SortableList
          label={t('groupsUi.list')}
          items={rows}
          getKey={(g) => g.id}
          getLabel={(g) => g.name}
          disabled={!canReorder || reorder.isPending}
          onChange={(next) => {
            qc.setQueryData(key, next);
            reorder.mutate(next.map((g) => g.id));
          }}
          renderItem={(g) => (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Link className="font-medium text-brand hover:underline" to={`/manage/groups/${g.id}`}>
                {g.name}
              </Link>
              <Badge tone={g.kind === 'repeat' ? 'good' : 'neutral'}>{t(`groupsUi.kind.${g.kind}`)}</Badge>
              {g.memberCount !== null ? <span className="text-sm text-slate-600">{t('groupsUi.members', { count: g.memberCount })}</span> : null}
              {g.hasKioskPin ? <Badge tone="warn">{t('groupsUi.pin')}</Badge> : null}
              {g.status === 'archived' ? <Badge>{t('groupsUi.archivedBadge')}</Badge> : null}
            </div>
          )}
        />
      )}
      {reorder.isPending ? <Alert kind="info">{t('common.loading')}</Alert> : null}
    </>
  );
}
